import type { CallTaskEventId } from "@repo/app-types/entities/callTaskEvent";
import type {
  CustomerTimelineEntry,
  CustomerTimelineTeammate,
} from "@repo/app-types/entities/performanceCustomer";
import type { RecordingId } from "@repo/app-types/entities/recording";
import {
  buildTeammateDisplayNames,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import type { CustomerTimelineRowDetailsParticipant } from "@repo/ui/customerTimeline/customerTimelineRowDetails";
import {
  teammateLabelKind,
  type TeammateLabelProps,
} from "@repo/ui/teammate/teammateLabel";
import { DateTime } from "luxon";
import type { ReactNode } from "react";

type CustomerInteractionTarget =
  | { recordingId: RecordingId }
  | { textConversationId: CallTaskEventId };

/**
 * Opens an interaction's slideover, at a call or text conversation if one is
 * given.
 */
export type OpenCustomerInteraction = (
  gradingId: GradingId,
  target?: CustomerInteractionTarget,
) => void;

/** What an interaction row draws: its icon on the rail, and its details. */
export interface InteractionRowParts {
  icon: ReactNode;
  details: ReactNode;
}

export interface CustomerTimelineRowOptions {
  openInteraction: OpenCustomerInteraction;
  /** The teammate whose page this is, if any. */
  viewedTeammateId: TeammateId | undefined;
}

/** A voice agent shows its persona name, with its configuration beside it. */
export function buildCustomerTimelineTeammateLabel(
  teammate: CustomerTimelineTeammate,
): TeammateLabelProps {
  const name = buildTeammateDisplayNames(teammate).primaryDisplayName;
  const kind = teammateLabelKind(teammate.type);
  // An agent without its own persona name uses its configuration name, so
  // show the name once.
  return teammate.persona_name && teammate.persona_name !== name
    ? { name: teammate.persona_name, kind, configurationName: name }
    : { name, kind };
}

export function buildCustomerTimelineParticipant(
  teammate: CustomerTimelineTeammate,
): CustomerTimelineRowDetailsParticipant {
  return { id: teammate.id, ...buildCustomerTimelineTeammateLabel(teammate) };
}

/** The id of an entry's row. */
export function buildEntryRowId(entry: CustomerTimelineEntry): string {
  return `${entry.type}:${entry.id}`;
}

/** "Sep 22, 4:51 PM", in the reader's timezone. */
export function formatCustomerTimelineTime(timestamp: string): string {
  return DateTime.fromISO(timestamp).toFormat("MMM d, h:mm a");
}

/** Muted text after a row's title, such as a call's length. */
export function renderTrailingText(text: string): ReactNode {
  return (
    <span className="text-xs font-normal text-muted-foreground">{text}</span>
  );
}
